<template>
  <div class="dashboard-container">
    <!-- Sidebar -->
    <aside class="sidebar" :class="{ 'sidebar-collapsed': isCollapsed }">
      <div class="sidebar-header">
        <button class="toggle-btn" @click="toggleSidebar">
          <span v-if="!isCollapsed">☰</span>
          <span v-else>→</span>
        </button>
        <h1 v-if="!isCollapsed" class="sidebar-title">Property Management</h1>
      </div>
      
      <nav class="sidebar-nav">
        <router-link to="/" class="nav-item" active-class="active">
          <span class="nav-icon">🏠</span>
          <span v-if="!isCollapsed" class="nav-text">Dashboard</span>
        </router-link>
        <router-link to="/reservations" class="nav-item" active-class="active">
          <span class="nav-icon">📋</span>
          <span v-if="!isCollapsed" class="nav-text">Reservations</span>
        </router-link>
        <router-link to="/properties" class="nav-item" active-class="active">
          <span class="nav-icon">🏨</span>
          <span v-if="!isCollapsed" class="nav-text">Properties</span>
        </router-link>
        <button @click="openEmailContactsModal" class="nav-item nav-button">
          <span class="nav-icon">📧</span>
          <span v-if="!isCollapsed" class="nav-text">Email Contacts</span>
        </button>
        <router-link to="/settings" class="nav-item" active-class="active">
          <span class="nav-icon">⚙️</span>
          <span v-if="!isCollapsed" class="nav-text">Settings</span>
        </router-link>
      </nav>
    </aside>

    <!-- Main Content -->
    <main class="main-content">
      <div class="content-header">
        <h1>Dashboard</h1>
        <div class="user-info">
          <span>Welcome back!</span>
          <button @click="logout" class="logout-btn">Logout</button>
        </div>
      </div>

      <!-- SECTION 1: Global Statistics -->
      <div class="global-stats-section">
        <h2 class="section-title">Global Statistics (All Properties)</h2>
        <div v-if="loadingGlobalStats" class="section-spinner">
          <div class="spinner"></div>
        </div>
        <div v-else class="kpi-grid">
          <div class="kpi-card">
            <div class="kpi-icon">🏨</div>
            <div class="kpi-content">
              <h3>Total Properties</h3>
              <div class="kpi-value">{{ globalStats.totalProperties || 0 }}</div>
            </div>
          </div>
          
          <div class="kpi-card">
            <div class="kpi-icon">📅</div>
            <div class="kpi-content">
              <h3>Arriving Today</h3>
              <div class="kpi-value">{{ globalStats.totalArrivingToday || 0 }}</div>
              <div class="kpi-subtitle">{{ formatCurrency(globalStats.totalArrivingTodayCost || 0) }}</div>
            </div>
          </div>
          
          <div class="kpi-card">
            <div class="kpi-icon">✅</div>
            <div class="kpi-content">
              <h3>Active Reservations</h3>
              <div class="kpi-value">{{ globalStats.totalActiveReservations || 0 }}</div>
              <div class="kpi-subtitle">{{ formatCurrency(globalStats.totalActiveReservationsCost || 0) }}</div>
            </div>
          </div>
          
          <div class="kpi-card global-revenue">
            <div class="kpi-icon">💰</div>
            <div class="kpi-content">
              <h3>This Month Revenue</h3>
              <div class="kpi-value">{{ formatCurrency(globalStats.totalMonthRevenue || 0) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- SECTION 2: Property Revenue Comparison -->
      <div class="revenue-comparison-section">
        <h2 class="section-title">Property Revenue Comparison</h2>
        
        <div v-if="loadingRevenueComparison" class="section-spinner">
          <div class="spinner"></div>
        </div>
        <div v-else>
          <!-- Property Selector (5 horizontal) -->
          <div class="property-selector">
            <div class="property-selector-title">Select Properties to Compare:</div>
            <div class="property-checkboxes">
              <label v-for="property in properties" :key="property.id" class="property-checkbox" :class="{ active: selectedProperties.includes(property.id) }">
                <input type="checkbox" :value="property.id" v-model="selectedProperties" @change="updateRevenueComparison" />
                <span class="property-name">{{ property.name }}</span>
              </label>
            </div>
          </div>

          <!-- Revenue Chart -->
          <div class="revenue-chart">
            <div v-if="selectedPropertiesData.length > 0" class="chart-container">
              <div v-for="property in selectedPropertiesData" :key="property.id" class="revenue-bar">
                <div class="bar-container">
                  <div class="bar" :style="{ height: getBarHeight(property.monthRevenue) + '%' }"></div>
                </div>
                <div class="property-info">
                  <div class="property-name">{{ property.name }}</div>
                  <div class="revenue-amount">{{ formatCurrency(property.monthRevenue || 0) }}</div>
                </div>
              </div>
            </div>
            <div v-else class="empty-state">
              <p>Select properties to compare their revenue</p>
            </div>
          </div>
        </div>
      </div>

      <!-- SECTION 3: Property-Specific Reservations -->
      <div class="reservations-section">
        <h2 class="section-title">Reservations by Property</h2>
        
        <div v-if="loadingPropertyReservations" class="section-spinner">
          <div class="spinner"></div>
        </div>
        <div v-else>
          <!-- Property Selector -->
          <div class="property-dropdown">
            <label for="property-select">Show reservations for:</label>
            <select id="property-select" v-model="selectedPropertyForReservations" @change="fetchSelectedPropertyReservations">
              <option v-for="property in properties" :key="property.id" :value="property.id">
                {{ property.name }}
              </option>
            </select>
          </div>

        <!-- Reservations Booked Today -->
        <div class="section-card">
          <div class="section-header">
            <h2>Reservations Arriving Today</h2>
            <span class="count-badge">{{ selectedPropertyData.arrivingTodayCount || 0 }}</span>
          </div>
          
          <div class="table-container" v-if="selectedPropertyData.arrivingToday && selectedPropertyData.arrivingToday.length > 0">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Guest Name</th>
                  <th>Check-in</th>
                  <th>Check-out</th>
                  <th>Nights</th>
                  <th>Total Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="reservation in selectedPropertyData.arrivingToday.slice(0, 5)" :key="reservation.id">
                  <td>{{ reservation.guestName }}</td>
                  <td>{{ formatDate(reservation.arrivalDate) }}</td>
                  <td>{{ formatDate(reservation.departureDate) }}</td>
                  <td class="text-center">{{ reservation.totalNights }}</td>
                  <td class="amount">{{ formatCurrency(reservation.totalReservationPrice) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div v-else class="empty-state">
            <p>No reservations arriving today for this property</p>
          </div>
        </div>

        <!-- Active Reservations -->
        <div class="section-card">
          <div class="section-header">
            <h2>Active Reservations</h2>
            <span class="count-badge">{{ selectedPropertyData.activeReservationsCount || 0 }}</span>
          </div>
          
          <div class="table-container" v-if="selectedPropertyData.activeReservations && selectedPropertyData.activeReservations.length > 0">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Guest Name</th>
                  <th>Check-in</th>
                  <th>Check-out</th>
                  <th>Nights</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="reservation in selectedPropertyData.activeReservations.slice(0, 10)" :key="reservation.id">
                  <td>{{ reservation.guestName }}</td>
                  <td>{{ formatDate(reservation.arrivalDate) }}</td>
                  <td>{{ formatDate(reservation.departureDate) }}</td>
                  <td class="text-center">{{ reservation.totalNights }}</td>
                  <td class="amount">{{ formatCurrency(reservation.totalReservationPrice) }}</td>
                  <td>
                    <span class="status-badge active">Active</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div v-else class="empty-state">
            <p>No active reservations for this property</p>
          </div>
          
          <div v-if="selectedPropertyData.activeReservations && selectedPropertyData.activeReservations.length > 10" class="view-all">
            <router-link to="/reservations" class="btn btn-outline">View All Reservations</router-link>
          </div>
        </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios';
import SETTINGS from '@/config/settings.js';

export default {
  name: 'DashboardView',
  data() {
    return {
      isCollapsed: false,
      baseUrl: SETTINGS.API.baseUrl,
      
      // Properties data
      properties: [],
      
      // Global statistics (aggregated from all properties)
      globalStats: {
        totalProperties: 0,
        totalArrivingToday: 0,
        totalArrivingTodayCost: 0,
        totalActiveReservations: 0,
        totalActiveReservationsCost: 0,
        totalMonthRevenue: 0
      },
      
      // Individual property data for calculations
      propertiesData: [],
      
      // Revenue comparison
      selectedProperties: [],
      selectedPropertiesData: [],
      maxRevenue: 0,
      
      // Selected property for reservations
      selectedPropertyForReservations: null,
      selectedPropertyData: {
        arrivingToday: [],
        arrivingTodayCount: 0,
        activeReservations: [],
        activeReservationsCount: 0
      },
      
      // Loading states
      loadingGlobalStats: true,
      loadingRevenueComparison: true,
      loadingPropertyReservations: true
    };
  },
  
  methods: {
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
    },
    
    logout() {
      localStorage.removeItem('token');
      this.$router.push('/login');
    },
    
    openEmailContactsModal() {
      this.$router.push('/reservations?openContacts=true');
    },
    
    formatCurrency(amount) {
      return SETTINGS.formatCurrency(amount || 0);
    },
    
    formatDate(timestamp) {
      return SETTINGS.formatDate(timestamp);
    },
    
    async fetchDashboardData() {
      const token = localStorage.getItem('token');
      if (!token) {
        this.$router.push('/login');
        return;
      }

      const headers = { 'Authorization': `Bearer ${token}` };
      this.loadingGlobalStats = true;

      try {
        // Step 1: Fetch all properties
        const propertiesRes = await axios.get(`${this.baseUrl}/properties`, { headers });
        this.properties = propertiesRes.data.properties.data;
        
        // Set default selected property for reservations (first property)
        if (this.properties.length > 0 && !this.selectedPropertyForReservations) {
          this.selectedPropertyForReservations = this.properties[0].id;
        }
        
        // Properties are loaded, revenue comparison can show selectors
        this.loadingRevenueComparison = false;
        
        // Step 2: Fetch data for each property separately with batching to avoid rate limiting
        this.propertiesData = [];
        const batchSize = 5; // Process 5 properties at a time
        const delayBetweenBatches = 1000; // 1 second delay between batches
        
        for (let i = 0; i < this.properties.length; i += batchSize) {
          const batch = this.properties.slice(i, i + batchSize);
          
          const batchPromises = batch.map(async (property) => {
            try {
              // Add small delay between individual requests within batch
              await new Promise(resolve => setTimeout(resolve, 200 * (i % batchSize)));
              
              const [arrivingTodayRes, activeTodayRes, monthRevenueRes] = await Promise.all([
                axios.get(`${this.baseUrl}/reservations/booked?propertyId=${property.id}`, { headers }),
                axios.get(`${this.baseUrl}/reservations/today?propertyId=${property.id}`, { headers }),
                axios.get(`${this.baseUrl}/reports/current?propertyId=${property.id}`, { headers })
              ]);
              
              const arrivingToday = arrivingTodayRes.data.reservations.data;
              const activeToday = activeTodayRes.data.reservations.data;
              const monthRevenue = Object.values(monthRevenueRes.data.reservations).reduce((total, res) => total + res.totalReservationPrice, 0);
              
              return {
                id: property.id,
                name: property.name,
                arrivingToday: arrivingToday,
                arrivingTodayCount: arrivingToday.length,
                arrivingTodayCost: arrivingToday.reduce((total, res) => total + res.totalReservationPrice, 0),
                activeReservations: activeToday,
                activeReservationsCount: activeToday.length,
                activeReservationsCost: activeToday.reduce((total, res) => total + res.totalReservationPrice, 0),
                monthRevenue: monthRevenue
              };
            } catch (error) {
              console.error(`Error fetching data for property ${property.name}:`, error);
              return {
                id: property.id,
                name: property.name,
                arrivingToday: [],
                arrivingTodayCount: 0,
                arrivingTodayCost: 0,
                activeReservations: [],
                activeReservationsCount: 0,
                activeReservationsCost: 0,
                monthRevenue: 0
              };
            }
          });
          
          // Process current batch
          const batchResults = await Promise.all(batchPromises);
          this.propertiesData.push(...batchResults);
          
          // Update global stats after each batch for progressive loading
          this.calculateGlobalStats();
          
          // After first batch, property reservations section can be shown
          if (i === 0) {
            this.loadingPropertyReservations = false;
            // Load selected property reservations
            this.fetchSelectedPropertyReservations();
          }
          
          // Add delay before next batch (except for last batch)
          if (i + batchSize < this.properties.length) {
            await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
          }
        }
        
        
        // Final step: Initialize selections and show global stats
        if (this.selectedProperties.length === 0 && this.properties.length > 0) {
          // Select first 3 properties by default for comparison
          this.selectedProperties = this.properties.slice(0, Math.min(3, this.properties.length)).map(p => p.id);
          this.updateRevenueComparison();
        }
        
        // All data loaded, show global statistics
        this.loadingGlobalStats = false;
        
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          this.$router.push('/login');
        }
      } finally {
        // Ensure all loading states are disabled even if there's an error
        this.loadingGlobalStats = false;
        this.loadingRevenueComparison = false;
        this.loadingPropertyReservations = false;
      }
    },
    
    calculateGlobalStats() {
      
      const totalArrivingToday = this.propertiesData?.reduce((total, prop) => total + (prop.arrivingTodayCount || 0), 0) || 0;
      const totalArrivingTodayCost = this.propertiesData?.reduce((total, prop) => total + (prop.arrivingTodayCost || 0), 0) || 0;
      const totalActiveReservations = this.propertiesData?.reduce((total, prop) => total + (prop.activeReservationsCount || 0), 0) || 0;
      const totalActiveReservationsCost = this.propertiesData?.reduce((total, prop) => total + (prop.activeReservationsCost || 0), 0) || 0;
      const totalMonthRevenue = this.propertiesData?.reduce((total, prop) => total + (prop.monthRevenue || 0), 0) || 0;
      
      
      // Ensure globalStats always has proper structure with default values
      this.globalStats = {
        totalProperties: this.properties?.length || 0,
        totalArrivingToday: totalArrivingToday,
        totalArrivingTodayCost: totalArrivingTodayCost,
        totalActiveReservations: totalActiveReservations,
        totalActiveReservationsCost: totalActiveReservationsCost,
        totalMonthRevenue: totalMonthRevenue
      };
      
    },
    
    updateRevenueComparison() {
      this.selectedPropertiesData = this.propertiesData.filter(prop => 
        this.selectedProperties.includes(prop.id)
      );
      
      // Calculate max revenue for chart scaling
      this.maxRevenue = Math.max(...this.selectedPropertiesData.map(p => p.monthRevenue), 1);
    },
    
    getBarHeight(revenue) {
      if (this.maxRevenue === 0) return 0;
      return (revenue / this.maxRevenue) * 100;
    },
    
    async fetchSelectedPropertyReservations() {
      if (!this.selectedPropertyForReservations) return;
      
      const propertyData = this.propertiesData.find(p => p.id == this.selectedPropertyForReservations);
      if (propertyData) {
        this.selectedPropertyData = {
          arrivingToday: propertyData.arrivingToday,
          arrivingTodayCount: propertyData.arrivingTodayCount,
          activeReservations: propertyData.activeReservations,
          activeReservationsCount: propertyData.activeReservationsCount
        };
      }
    },
    
    initializeDefaults() {
      // Initialize all data structures to prevent undefined errors
      this.globalStats = {
        totalProperties: 0,
        totalArrivingToday: 0,
        totalArrivingTodayCost: 0,
        totalActiveReservations: 0,
        totalActiveReservationsCost: 0,
        totalMonthRevenue: 0
      };
      
      this.selectedPropertyData = {
        arrivingToday: [],
        arrivingTodayCount: 0,
        activeReservations: [],
        activeReservationsCount: 0
      };
      
      // Initialize loading states
      this.loadingGlobalStats = true;
      this.loadingRevenueComparison = true;
      this.loadingPropertyReservations = true;
    }
  },
  
  created() {
    // Ensure globalStats is properly initialized even before API calls
    this.initializeDefaults();
  },
  
  mounted() {
    this.fetchDashboardData();
  }
};
</script>

<style scoped>
.dashboard-container {
  display: flex;
  min-height: 100vh;
  background: #f8fafc;
}

/* Sidebar Styles */
.sidebar {
  width: 280px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  transition: width 0.3s ease;
  position: fixed;
  height: 100vh;
  z-index: 1000;
  box-shadow: 2px 0 10px rgba(0,0,0,0.1);
}

.sidebar-collapsed {
  width: 70px;
}

.sidebar-header {
  padding: 20px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
}

.toggle-btn {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  margin-bottom: 15px;
}

.sidebar-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.sidebar-nav {
  padding: 20px 0;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  color: rgba(255,255,255,0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  margin: 0 10px;
  border-radius: 10px;
}

.nav-item:hover,
.nav-item.active {
  background: rgba(255,255,255,0.1);
  color: white;
  transform: translateX(5px);
}

.nav-button {
  background: none;
  border: none;
  width: 100%;
  cursor: pointer;
  text-align: left;
}

.nav-icon {
  font-size: 18px;
  margin-right: 15px;
  width: 20px;
}

.nav-text {
  font-weight: 500;
}

/* Main Content */
.main-content {
  flex: 1;
  margin-left: 280px;
  padding: 30px;
  transition: margin-left 0.3s ease;
}

.sidebar-collapsed + .main-content {
  margin-left: 70px;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.content-header h1 {
  font-size: 32px;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.logout-btn {
  background: #e53e3e;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.3s ease;
}

.logout-btn:hover {
  background: #c53030;
}

/* KPI Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 25px;
  margin-bottom: 40px;
}

.kpi-card {
  background: white;
  padding: 25px;
  border-radius: 16px;
  box-shadow: 0 4px 25px rgba(0,0,0,0.08);
  display: flex;
  align-items: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid #e2e8f0;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 35px rgba(0,0,0,0.12);
}

.kpi-icon {
  font-size: 40px;
  margin-right: 20px;
  opacity: 0.8;
}

.kpi-content h3 {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.kpi-value {
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 4px;
}

.kpi-subtitle {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

/* Section Cards */
.section-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 25px rgba(0,0,0,0.08);
  margin-bottom: 30px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.section-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1a202c;
}

.count-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
}

/* Table Styles */
.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  background: #f8fafc;
  padding: 16px 20px;
  text-align: left;
  font-weight: 600;
  color: #4a5568;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e2e8f0;
}

.data-table td {
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  color: #2d3748;
  vertical-align: middle;
}

.data-table tbody tr:hover {
  background: #f7fafc;
}

.property-name {
  font-weight: 600;
  color: #2b6cb0;
}

.amount {
  text-align: right;
  font-weight: 600;
  color: #22543d;
}

.text-center {
  text-align: center;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.active {
  background: #c6f6d5;
  color: #22543d;
}

/* Empty State */
.empty-state {
  padding: 60px 20px;
  text-align: center;
  color: #64748b;
}

.empty-state p {
  font-size: 16px;
  margin: 0;
}

/* View All */
.view-all {
  padding: 20px 30px;
  text-align: center;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.btn {
  display: inline-flex;
  align-items: center;
  padding: 10px 24px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.btn-outline {
  background: transparent;
  color: #667eea;
  border-color: #667eea;
}

.btn-outline:hover {
  background: #667eea;
  color: white;
  transform: translateY(-1px);
}

/* Section Styles */
.global-stats-section,
.revenue-comparison-section,
.reservations-section {
  margin-bottom: 40px;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 20px 0;
  text-align: center;
}

/* Global Revenue Card Special Style */
.global-revenue {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.global-revenue .kpi-content h3,
.global-revenue .kpi-value {
  color: white;
}

/* Property Selector Styles */
.property-selector {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 25px rgba(0,0,0,0.08);
  border: 1px solid #e2e8f0;
}

.property-selector-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 15px;
}

.property-checkboxes {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.property-checkbox {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.property-checkbox:hover {
  border-color: #667eea;
  background: #f1f5f9;
}

.property-checkbox.active {
  border-color: #667eea;
}

.property-checkbox input[type="checkbox"] {
  margin-right: 8px;
  accent-color: #667eea;
}

.property-checkbox .property-name {
  font-weight: 500;
}

/* Revenue Chart Styles */
.revenue-chart {
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 25px rgba(0,0,0,0.08);
  border: 1px solid #e2e8f0;
}

.chart-container {
  display: flex;
  align-items: end;
  justify-content: space-around;
  height: 300px;
  padding: 20px 0;
  border-bottom: 2px solid #e2e8f0;
}

.revenue-bar {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 120px;
}

.bar-container {
  height: 200px;
  width: 60px;
  display: flex;
  align-items: end;
  margin-bottom: 15px;
}

.bar {
  width: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 6px 6px 0 0;
  transition: all 0.3s ease;
  min-height: 10px;
}

.property-info {
  text-align: center;
}

.property-info .property-name {
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 5px;
}

.property-info .revenue-amount {
  font-size: 14px;
  color: #667eea;
  font-weight: 600;
}

/* Property Dropdown Styles */
.property-dropdown {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 25px rgba(0,0,0,0.08);
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 15px;
}

.property-dropdown label {
  font-weight: 600;
  color: #1a202c;
  white-space: nowrap;
}

.property-dropdown select {
  flex: 1;
  padding: 10px 15px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  font-size: 14px;
  font-weight: 500;
  color: #1a202c;
  cursor: pointer;
  transition: border-color 0.3s ease;
}

.property-dropdown select:focus {
  outline: none;
  border-color: #667eea;
}

/* Responsive Design */
@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }
  
  .main-content {
    margin-left: 0;
    padding: 20px 15px;
  }
  
  .kpi-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .global-revenue {
    grid-column: 1;
  }
  
  .content-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .section-header {
    padding: 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .data-table {
    font-size: 12px;
  }
  
  .data-table th,
  .data-table td {
    padding: 12px 8px;
  }
  
  .property-checkboxes {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .chart-container {
    flex-wrap: wrap;
    height: auto;
    gap: 20px;
  }
  
  .revenue-bar {
    min-width: 80px;
  }
  
  .bar-container {
    height: 150px;
    width: 40px;
  }
  
  .property-dropdown {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  
  .property-dropdown label {
    white-space: normal;
  }
}

/* Section Spinner */
.section-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  min-height: 200px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>